import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Instagram, Twitter } from "lucide-react";
import InViewReveal from "./InViewReveal";
import SectionWatermark from "./SectionWatermark";
import CatalogueCTA from "./CatalogueCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSTAGRAM_URL = "https://www.instagram.com/viloraimpex?igsh=Z3VzOXY0ZHYzeWJr";
const X_URL = "https://x.com/viloraimpex";

const PRODUCTS = ["Rice & Grain", "Spice & Masala", "Dry Fruit & Pulse", "Fresh Produce", "Multi-category", "Other"];

/**
 * Inquiry - typewriter-form aesthetic. Fields are hairline-underlined, mono
 * labels, layout reads like a typed correspondence form. On submit, the
 * card rotates in a small "RECEIVED" stamp.
 */
export default function InquirySection() {
  const [form, setForm] = useState({ name:"", company:"", email:"", country:"", product_interest:"", message:"" });
  const [submitting, setSubmitting] = useState(false);
  const [received, setReceived] = useState(false);
  const socialRef = useRef(null);
  const [socialCue, setSocialCue] = useState(false);
  const selectedProducts = form.product_interest
    ? form.product_interest.split(",").map((item) => item.trim()).filter(Boolean)
    : [];

  useEffect(() => {
    const node = socialRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setSocialCue(true);
        timer = window.setTimeout(() => setSocialCue(false), 2000);
        observer.disconnect();
      },
      { threshold: 0.45 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleProduct = (product) => {
    setForm((current) => {
      const currentProducts = current.product_interest
        ? current.product_interest.split(",").map((item) => item.trim()).filter(Boolean)
        : [];
      const nextProducts = currentProducts.includes(product)
        ? currentProducts.filter((item) => item !== product)
        : [...currentProducts, product];
      return { ...current, product_interest: nextProducts.join(", ") };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setReceived(true);
      toast.success("Inquiry received. We will write back within one business day.");
      setForm({ name:"", company:"", email:"", country:"", product_interest:"", message:"" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="inquiry" className="relative paper" style={{ overflow: "hidden" }}>
      <SectionWatermark variant="monogram" position="top-right" size={420} opacity={0.18} />
      <div className="relative px-6 sm:px-10 py-14 md:py-20" style={{ zIndex: 1 }}>

        <InViewReveal className="flex items-baseline gap-4 mb-8 md:mb-12">
          <span className="folio">№ 06</span>
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
          <span className="folio" style={{ color: "var(--ink-mute)" }}>OPEN A FILE</span>
        </InViewReveal>

        <div className="grid md:grid-cols-12 gap-10 md:gap-16">

          {/* Left - column of words */}
          <InViewReveal delay={2} className="md:col-span-5">
            <h2 className="display" style={{ fontSize: "clamp(40px, 5vw, 72px)", lineHeight: 1, letterSpacing: "-0.02em" }}>
              Send us
              <br/>
              <em className="display-italic" style={{ color: "var(--vermilion)" }}>a few lines.</em>
            </h2>
            <p className="mt-6" style={{ fontSize: "clamp(17px, 1.25vw, 20px)", lineHeight: 1.65, color: "var(--ink-soft)", maxWidth: "44ch" }}>
              Destination port, category, approximate volume, and a date you
              would like the cargo to arrive. We will write back the same day
              with a price, a sample plan, and the documents we will need from you.
            </p>

            {/* Why-we-ask block - NN/g: explaining the reason for an info
                request raises compliance and perceived legitimacy. */}
            <div className="v-inquiry-whyask">
              <div className="v-inquiry-whyask-title">Why we ask for these details</div>
              <ul className="v-inquiry-whyask-list">
                <li><span className="dot">·</span> Destination port → tells us which inspections, papers, and shipping line apply.</li>
                <li><span className="dot">·</span> Volume → drives mill allocation and a sample plan.</li>
                <li><span className="dot">·</span> Target date → so we book reefer / dry container before the slot fills.</li>
              </ul>
              <div className="v-inquiry-whyask-foot">
                Your details are used to reply only. We do not enrol you in any list.
              </div>
            </div>

            <div className="rule-engraved my-10" />

            {/* Contact details - ledger-style rows with dotted underlines */}
            <div className="mb-3 flex items-center gap-3">
              <span className="status-dot status-dot--live" />
              <span className="folio">DESK · OPEN NOW</span>
            </div>
            <div
              ref={socialRef}
              className={`paper p-4 social-contact-card ${socialCue ? "social-cta-active" : ""}`}
              style={{ background: "var(--paper-2)", border: "1px solid var(--rule-soft)" }}
            >
              {socialCue ? (
                <div className="social-follow-message social-follow-message--light">
                  Follow us on Instagram
                </div>
              ) : null}
              <div className="ledger-row">
                <span className="folio" style={{ color: "var(--ink-mute)" }}>Export desk</span>
                <a href="mailto:exports@viloraimpex.com" className="hover:text-[var(--vermilion)] transition-colors" style={{ fontSize: 16 }}>exports@viloraimpex.com</a>
              </div>
              <div className="ledger-row">
                <span className="folio" style={{ color: "var(--ink-mute)" }}>Voice</span>
                <a href="tel:+918669999901" className="hover:text-[var(--vermilion)] transition-colors mono" style={{ fontSize: 15.5, letterSpacing: "0.04em" }}>+91 86699 99901</a>
              </div>
              <div className="ledger-row">
                <span className="folio" style={{ color: "var(--ink-mute)" }}>Instagram</span>
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="social-inline-link social-cta-link">
                  <Instagram size={15} strokeWidth={1.6} />
                  <span>@viloraimpex</span>
                  <ExternalLink size={12} strokeWidth={1.7} />
                  <span className="clickable-pill clickable-pill--light">Open</span>
                </a>
              </div>
              <div className="ledger-row">
                <span className="folio" style={{ color: "var(--ink-mute)" }}>X</span>
                <a href={X_URL} target="_blank" rel="noopener noreferrer" className="social-inline-link social-cta-link">
                  <Twitter size={15} strokeWidth={1.6} />
                  <span>@viloraimpex</span>
                  <ExternalLink size={12} strokeWidth={1.7} />
                  <span className="clickable-pill clickable-pill--light">Open</span>
                </a>
              </div>
              <div className="ledger-row">
                <span className="folio" style={{ color: "var(--ink-mute)" }}>House</span>
                <span style={{ fontSize: 16 }}>Rajkot, Gujarat - India</span>
              </div>
              <div className="ledger-row">
                <span className="folio" style={{ color: "var(--ink-mute)" }}>Hours</span>
                <span className="mono" style={{ fontSize: 14.5, color: "var(--ink-soft)", letterSpacing: "0.04em" }}>Mon–Sat · 09:00–19:00 IST</span>
              </div>
            </div>
            <CatalogueCTA className="mt-5" code="BEFORE YOU WRITE" compact />
          </InViewReveal>

          {/* Right - typewriter form */}
          <InViewReveal delay={3} className="md:col-span-7 lg:col-span-6 lg:col-start-7 relative">
            {/* "RECEIVED" stamp overlay */}
            {received && (
              <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                <div
                  className="stamp-anim"
                  style={{
                    border: "3px double var(--vermilion)",
                    color: "var(--vermilion)",
                    padding: "16px 32px",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 600,
                    fontSize: 26,
                    letterSpacing: "0.32em",
                    transform: "rotate(-6deg)",
                    background: "rgba(244, 239, 227, 0.85)",
                  }}
                >
                  RECEIVED · {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()}
                </div>
              </div>
            )}

            <form
              onSubmit={submit}
              className="relative border p-6 sm:p-10 paper overflow-hidden"
              style={{ borderColor: "var(--rule)", background: "var(--paper-1)" }}
            >
              {/* Watermark - house logo embossed into the form's paper */}
              <img
                src="/logo.png"
                alt=""
                aria-hidden="true"
                className="absolute pointer-events-none select-none"
                style={{
                  right: -64,
                  bottom: -64,
                  width: 380,
                  height: 380,
                  opacity: 0.045,
                  filter: "grayscale(1) contrast(1.4)",
                  zIndex: 0,
                }}
              />
              <div className="relative z-10">
              {/* Form folio header */}
              <div className="flex items-baseline justify-between mb-8 pb-4 border-b" style={{ borderColor: "var(--rule)" }}>
                <div>
                  <div className="folio">FORM A - INQUIRY</div>
                  <div className="meta mt-1">FILE NO · {Math.floor(Math.random() * 9000) + 1000}-{new Date().getFullYear()}</div>
                </div>
                <div className="meta hidden sm:block">{new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}</div>
              </div>

              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                <Field label="Name *"        value={form.name}    onChange={set("name")} placeholder="Sayed K. Al Mansoori"/>
                <Field label="Company"        value={form.company} onChange={set("company")} placeholder="Trading firm"/>
                <Field label="Email *"        value={form.email}   onChange={set("email")} placeholder="you@firm.com" type="email"/>
                <Field label="Country / port" value={form.country} onChange={set("country")} placeholder="UAE - Jebel Ali"/>
              </div>

              <div className="mt-6">
                <label className="folio block mb-3">Category of interest</label>
                <div className="flex flex-wrap gap-2">
                  {PRODUCTS.map((p) => {
                    const active = selectedProducts.includes(p);
                    return (
                      <button
                        type="button"
                        key={p}
                        onClick={() => toggleProduct(p)}
                        aria-pressed={active}
                        className="mono px-3 py-1.5 transition-all"
                        style={{
                          fontSize: 12,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          border: `1px solid ${active ? "var(--vermilion)" : "var(--rule)"}`,
                          color: active ? "var(--vermilion)" : "var(--ink-soft)",
                          background: active ? "rgba(200, 67, 31, 0.06)" : "transparent",
                        }}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6">
                <label className="folio block mb-2">Requirement *</label>
                <textarea
                  className="field"
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Volume, packaging, port of arrival, target date…"
                  required
                />
              </div>

              <div className="rule mt-6 mb-6" />

              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="meta">
                  We respond within one business day.
                </div>
                <button type="submit" disabled={submitting} className="btn-accent disabled:opacity-50">
                  <span>{submitting ? "Sending…" : "Send Inquiry"}</span>
                  <span className="btn-arrow">
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path d="M1 5h10M7 1l4 4-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="square"/>
                    </svg>
                  </span>
                </button>
              </div>

              <div className="v-inquiry-next">
                <div className="v-inquiry-next-title">What happens next</div>
                <ol className="v-inquiry-next-list">
                  <li className="v-inquiry-next-step">
                    <span className="num">01</span>
                    <span>We confirm receipt and assign a file number within the hour.</span>
                  </li>
                  <li className="v-inquiry-next-step">
                    <span className="num">02</span>
                    <span>A reply within one business day with price, sample plan, and required documents.</span>
                  </li>
                  <li className="v-inquiry-next-step">
                    <span className="num">03</span>
                    <span>On agreement, we open a shipment file - sourcing, lab tests, packaging, sailing.</span>
                  </li>
                </ol>
              </div>
              </div>
            </form>
          </InViewReveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="folio block mb-2">{label}</label>
      <input
        className="field"
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </div>
  );
}
